import _ from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  AsyncStatus,
  ResourceMap,
  ResourceTableStatusesEnum,
} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {IconTextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/iconTextBox';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {SidePanelHeader} from '../sidePanelHeader';
import {SidePanelApplicationsManagerContent} from './sidePanelApplicationsManagerContent';

/*
 * Constants.
 */

const debounceDelay = 500;

/*
 * Props.
 */

interface SidePanelApplicationsManagerProps {
  isInfoBoxExpanded: boolean;
  pinnedFrames: ReadonlyArray<SidePanelFrame>;
  unpinnedFrames: ReadonlyArray<SidePanelFrame>;
  integrationTypes: ResourceMap<IntegrationTypeModel>;
  integrationTypesAsyncStatus: AsyncStatus;
  onToggleInfoBox: () => void;
  onTogglePinned: (frame: SidePanelFrame) => void;
  fetchIntegrationTypesAsync: () => Promise<void>;
  applicationsAsyncStatus: AsyncStatus;
  fetchApplicationsAsync: () => Promise<void>;
}

/*
 * Style.
 */

const StyledIconButton = styled(IconButton)`
  margin-right: 4px;
`;

const StyledSidePanelContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 8px 16px;
  overflow-y: auto;
`;

/*
 * Component.
 */

export const SidePanelApplicationsManager: FC<SidePanelApplicationsManagerProps> = (props) => {
  const {
    isInfoBoxExpanded,
    pinnedFrames,
    unpinnedFrames,
    integrationTypes,
    integrationTypesAsyncStatus,
    onToggleInfoBox,
    onTogglePinned,
    fetchIntegrationTypesAsync,
    fetchApplicationsAsync,
    applicationsAsyncStatus,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const localize = useLocalize();
  const isSearching = Boolean(searchQuery);
  const isSearchEnabled =
    integrationTypesAsyncStatus.status === ResourceTableStatusesEnum.LOADED &&
    applicationsAsyncStatus.status === ResourceTableStatusesEnum.LOADED;

  useInitialEffect(() => {
    fetchIntegrationTypesAsync();
  });

  const filteredPinnedFrames = useMemo(
    () =>
      _([...pinnedFrames])
        .filter((frame) => shouldDisplayFrame(searchQuery, frame, integrationTypes))
        .value(),
    [integrationTypes, pinnedFrames, searchQuery],
  );
  const filteredUnpinnedFrames = useMemo(
    () =>
      _([...unpinnedFrames])
        .filter((frame) => shouldDisplayFrame(searchQuery, frame, integrationTypes))
        .value(),
    [unpinnedFrames, searchQuery, integrationTypes],
  );

  const onSearchQueryUpdatedDebounced = useDebounce((value: string) => {
    setSearchQuery(value);
    trackInterfaceEvent(InterfaceEventsEnum.APPS_P4_SEARCH, {query: value});
  }, debounceDelay);

  return (
    <>
      <SidePanelHeader
        title={<Trans id="sidepanel-applications-manager-title">Manage</Trans>}
        additionalActionsAfter={renderAdditionalActionsAfter(isInfoBoxExpanded, isSearching, onToggleInfoBox)}
      >
        <IconTextBox
          iconName="searchSmall"
          placeholder={localize(
            defineMessage({
              id: 'sidepanel-applications-manager-search-placeholder',
              message: 'Search enabled plugins',
            }),
          )}
          onChange={onSearchQueryUpdatedDebounced}
          isDisabled={!isSearchEnabled}
        />
      </SidePanelHeader>
      <StyledSidePanelContentWrapperDiv>
        <SidePanelApplicationsManagerContent
          isInfoBoxExpanded={isInfoBoxExpanded}
          pinnedFrames={filteredPinnedFrames}
          unpinnedFrames={filteredUnpinnedFrames}
          integrationTypes={integrationTypes}
          integrationTypesAsyncStatus={integrationTypesAsyncStatus}
          applicationsAsyncStatus={applicationsAsyncStatus}
          isSearching={isSearching}
          onToggleInfoBox={onToggleInfoBox}
          onTogglePinned={onTogglePinned}
          fetchIntegrationTypesAsync={fetchIntegrationTypesAsync}
          fetchApplicationsAsync={fetchApplicationsAsync}
        />
      </StyledSidePanelContentWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function shouldDisplayFrame(
  searchQuery: string,
  frame: SidePanelFrame,
  integrationTypes: ResourceMap<IntegrationTypeModel>,
) {
  if (!searchQuery) {
    return true;
  }
  const normalizedSearchQuery = searchQuery.toLocaleLowerCase().trim();
  const integrationType = findIntegrationType(frame, integrationTypes);

  if (isFrameMatch(integrationTypes, normalizedSearchQuery, frame)) {
    return true;
  }
  if (integrationType && isIntegrationTypeMatch(normalizedSearchQuery, integrationType)) {
    return true;
  }
  return false;
}

function findIntegrationType(frame: SidePanelFrame, integrationTypes: ResourceMap<IntegrationTypeModel>) {
  if (frame.frameType === FrameTypesEnum.INTEGRATION) {
    return integrationTypes[frame.type];
  }
  if (frame.frameType === FrameTypesEnum.ENTRY_POINT) {
    return integrationTypes[frame.applicationTypeId];
  }
  return undefined;
}

function renderAdditionalActionsAfter(
  isInfoBoxExpanded: boolean,
  isSearching: boolean,
  onToggleInfoBox: () => void,
) {
  if (isSearching) {
    return undefined;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconButton
          iconName={isInfoBoxExpanded ? 'infoFilled' : 'info'}
          label={null}
          activeColor={theme.palette.blue.shade40}
          isActive={isInfoBoxExpanded}
          onClick={onToggleInfoBox}
        />
      )}
    </UserThemeConsumer>
  );
}

function isFrameMatch(
  integrationTypes: ResourceMap<IntegrationTypeModel>,
  searchQuery: string,
  frame: SidePanelFrame,
) {
  if (!searchQuery) {
    return true;
  }

  switch (frame.frameType) {
    case FrameTypesEnum.ENTRY_POINT:
    case FrameTypesEnum.INTEGRATION: {
      // Check the integration name first.
      if (frame.name.toLowerCase().includes(searchQuery)) {
        return true;
      }

      // Then check the integration type.
      const integrationType = integrationTypes[frame.type];
      return Boolean(integrationType && isIntegrationTypeMatch(searchQuery, integrationType));
    }

    case FrameTypesEnum.CUSTOMER_VIEW:
    case FrameTypesEnum.CALENDAR:
    case FrameTypesEnum.KEYBOARD_HELP:
    case FrameTypesEnum.START_GUIDE:
    case FrameTypesEnum.KNOWLEDGE_BASE:
    case FrameTypesEnum.APPLICATION_ENTRY_POINT:
      return frame.name.toLowerCase().includes(searchQuery);

    default:
      return false;
  }
}

function isIntegrationTypeMatch(searchQuery: string, integrationType: IntegrationTypeModel) {
  if (integrationType.name.toLowerCase().includes(searchQuery)) {
    return true;
  }

  return integrationType.searchKeywords.some((k) => k.toLowerCase().includes(searchQuery));
}
